import * as SentryVue from '@sentry/vue'
import * as SentryBrowser from '@sentry/browser'

import { nearAppTransition } from '@/utils/app-state'

const PLATFORM_NATIVE_IOS = 'ios'
const PLATFORM_NATIVE_ANDROID = 'android'
const PLATFORM_EXTENSION = 'extension'
const PLATFORM_WEB = 'web'

const TAG_PLATFORM = 'platform'
const TAG_BUILD_TARGET = 'build_target'
const TAG_BROWSER = 'browser'
const TAG_OS = 'os'
const TAG_APP_VERSION = 'app_version'
const TAG_GIT_SHA = 'git_sha'
const TAG_UNHANDLED = 'unhandled'

const CONTEXT_COLLECTION = 'collection'

const IGNORED_ERROR_MESSAGES = [
  'Invalid token specified',
  'SESSION_EXPIRED',
  'INVALID_TOKEN',
  'WRONG_APP_VERSION',
  'UNDER_MAINTENANCE',
]
const ERR_NETWORK_CODE = 'ERR_NETWORK'

const SCRUB_HEADER_KEYS = ['authorization', 'Authorization']
const SCRUB_BODY_KEYS = ['accessToken', 'refreshToken', 'password', 'token']
const SCRUB_PLACEHOLDER = '[Filtered]'

function getSentryClient() {
  if (BUILD_TARGET === 'extension') return SentryBrowser
  return SentryVue
}

export function isSentryEnabled() {
  return Boolean(getSentryClient().getClient?.())
}

export function setSentryUser(user) {
  if (!isSentryEnabled()) return
  if (!user || !user.id) {
    getSentryClient().setUser(null)
    return
  }
  getSentryClient().setUser({
    id: String(user.id),
    email: user.email ?? undefined,
    username: user.username ?? undefined,
  })
}

export function clearSentryUser() {
  if (!isSentryEnabled()) return
  getSentryClient().setUser(null)
  getSentryClient().setContext(CONTEXT_COLLECTION, null)
}

export function setSentryCollection(collectionId) {
  if (!isSentryEnabled()) return
  if (!collectionId) {
    getSentryClient().setContext(CONTEXT_COLLECTION, null)
    return
  }
  getSentryClient().setContext(CONTEXT_COLLECTION, { id: String(collectionId) })
}

function resolvePlatform(deviceState) {
  if (BUILD_TARGET === 'extension') return PLATFORM_EXTENSION
  if (deviceState?.platform === PLATFORM_NATIVE_IOS) return PLATFORM_NATIVE_IOS
  if (deviceState?.platform === PLATFORM_NATIVE_ANDROID) return PLATFORM_NATIVE_ANDROID
  return PLATFORM_WEB
}

export function applyBaseTags(deviceState = {}) {
  if (!isSentryEnabled()) return
  const client = getSentryClient()
  const tags = {
    [TAG_PLATFORM]: resolvePlatform(deviceState),
    [TAG_BUILD_TARGET]: BUILD_TARGET,
    [TAG_APP_VERSION]: APPVERSION,
    [TAG_GIT_SHA]: GITSHA,
  }
  if (deviceState?.os) tags[TAG_OS] = deviceState.os
  if (deviceState?.browser) tags[TAG_BROWSER] = deviceState.browser
  client.setTags(tags)
}

function toErrorInstance(error) {
  if (error instanceof Error) return error
  // Axios rejections from our interceptor are plain objects shaped like
  // { message, stack, code }. Wrap them so Sentry captures a real exception
  // with grouping/stacktrace working correctly.
  const message = error?.message ?? (typeof error === 'string' ? error : 'Unknown error')
  const wrapped = new Error(String(message))
  if (typeof error?.stack === 'string') wrapped.stack = error.stack
  if (error?.name) wrapped.name = error.name
  return wrapped
}

export function captureHandledError(error) {
  if (!isSentryEnabled()) return
  getSentryClient().captureException(toErrorInstance(error), {
    level: 'error',
    tags: { [TAG_UNHANDLED]: 'no' },
    extra: {
      messageKey: error?.message ?? null,
      code: error?.code ?? null,
      statusCode: error?.statusCode ?? error?.response?.status ?? null,
    },
  })
}

export function captureUntranslatedError(messageKey, error) {
  if (!isSentryEnabled()) return
  getSentryClient().captureMessage(`Untranslated error: ${messageKey}`, {
    level: 'warning',
    extra: {
      messageKey,
      originalError: error?.message ?? null,
      code: error?.code ?? null,
    },
  })
}

function scrubObjectKeys(target, keys) {
  if (!target || typeof target !== 'object') return
  for (const key of Object.keys(target)) {
    if (keys.includes(key)) {
      target[key] = SCRUB_PLACEHOLDER
    }
  }
}

function scrubRequestData(request) {
  if (!request) return
  scrubObjectKeys(request.headers, SCRUB_HEADER_KEYS)
  if (typeof request.data === 'object') {
    scrubObjectKeys(request.data, SCRUB_BODY_KEYS)
  }
  if (typeof request.cookies === 'object') {
    request.cookies = SCRUB_PLACEHOLDER
  }
}

function isIgnoredError(event) {
  const exception = event?.exception?.values?.[0]
  const value = exception?.value
  const code = event?.extra?.code
  if (value && IGNORED_ERROR_MESSAGES.includes(value)) return true
  // Only drop ERR_NETWORK when the device is genuinely offline; backend outages
  // produce ERR_NETWORK while online and we want those captured.
  if (
    code === ERR_NETWORK_CODE
    && typeof navigator !== 'undefined'
    && navigator.onLine === false
  ) return true
  const messageKey = event?.extra?.messageKey
  if (messageKey && IGNORED_ERROR_MESSAGES.includes(messageKey)) return true
  // Background timeout/network errors that slip through as unhandled rejections.
  // extra.code is absent there, so match the message too. See app-state.js.
  const isNoResponseTransient =
    code === ERR_NETWORK_CODE
    || code === 'ECONNABORTED'
    || (value && (value === 'Network Error' || /^timeout of \d+ms exceeded$/.test(value)))
  if (isNoResponseTransient && nearAppTransition()) return true
  return false
}

export function buildBeforeSend() {
  return event => {
    if (isIgnoredError(event)) return null
    scrubRequestData(event?.request)
    return event
  }
}

export function buildBeforeBreadcrumb() {
  return breadcrumb => {
    if (breadcrumb.category === 'ui.click') return null
    if (breadcrumb.data) {
      scrubObjectKeys(breadcrumb.data, SCRUB_HEADER_KEYS)
      scrubObjectKeys(breadcrumb.data, SCRUB_BODY_KEYS)
    }
    return breadcrumb
  }
}

export function addHttpBreadcrumb({ method, url, statusCode }) {
  if (!isSentryEnabled()) return
  getSentryClient().addBreadcrumb({
    category: 'http',
    type: 'http',
    level: statusCode && statusCode >= 400 ? 'error' : 'info',
    data: {
      method: method?.toUpperCase?.() ?? method,
      url,
      status_code: statusCode,
    },
  })
}
